import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';

const VPSList = ({ onVPSSelect, onCreateNew }) => {
  const [vpsList, setVpsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('nickname');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  const [filterActive, setFilterActive] = useState('all'); // 'all' | 'active' | 'inactive'
  const [copiedSSHId, setCopiedSSHId] = useState(null);
  const [copiedPwdId, setCopiedPwdId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    fetchVPSs();
  }, [pagination.page, searchTerm]);

  const fetchVPSs = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiRequest(
        `/admin/vps?page=${pagination.page}&limit=${pagination.limit}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`
      );
      if (data && data.success) {
        setVpsList(data.data || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
          pages: data.pagination?.pages || 0,
        }));
      } else {
        setError(data.message || 'حدث خطأ في جلب خوادم VPS');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
      console.error('Error fetching VPSs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleCopySSH = (vps) => {
    const cmd = `ssh ${vps.user}@${vps.ip}`;
    navigator.clipboard.writeText(cmd).then(() => {
      setCopiedSSHId(vps._id);
      setTimeout(() => setCopiedSSHId(null), 1500);
    }).catch(() => {
      setCopiedSSHId(null);
    });
  };

  const handleCopyPassword = (vps) => {
    const pwd = vps.password || '';
    if (!pwd) {
      return;
    }
    navigator.clipboard.writeText(pwd).then(() => {
      setCopiedPwdId(vps._id);
      setTimeout(() => setCopiedPwdId(null), 1500);
    }).catch(() => {
      setCopiedPwdId(null);
    });
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filtered = vpsList.filter((vps) => {
    if (filterActive === 'active' && !vps.isActive) return false;
    if (filterActive === 'inactive' && vps.isActive) return false;
    if (!normalizedSearch) return true;
    const fields = [
      vps.nickname || '',
      vps.ip || '',
      vps.user || '',
      vps.provider || '',
    ].map((s) => (s || '').toLowerCase());
    return fields.some((f) => f.includes(normalizedSearch));
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    const getVal = (obj) => {
      if (sortField === 'customerCount') return obj.customerCount || 0;
      if (sortField === 'startDate') return obj.startDate ? new Date(obj.startDate).getTime() : 0;
      if (sortField === 'endDate') return obj.endDate ? new Date(obj.endDate).getTime() : 0;
      return (obj[sortField] || '').toString().toLowerCase();
    };
    const va = getVal(a);
    const vb = getVal(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });

  if (loading && vpsList.length === 0) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        جاري التحميل...
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">خوادم VPS</h2>
          <div className="flex gap-2" style={{ alignItems: 'center' }}>
            <input
              type="text"
              placeholder="بحث (Nickname, IP, User, Provider)..."
              value={searchTerm}
              onChange={handleSearch}
              className="form-control"
              style={{ width: '250px' }}
            />
            <select className="form-control" value={sortField} onChange={(e) => setSortField(e.target.value)} style={{ width: '180px' }}>
              <option value="nickname">Nickname</option>
              <option value="ip">IP</option>
              <option value="user">User</option>
              <option value="provider">Provider</option>
              <option value="customerCount">عدد العملاء</option>
              <option value="startDate">تاريخ البدء</option>
              <option value="endDate">تاريخ الانتهاء</option>
            </select>
            <select className="form-control" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={{ width: '120px' }}>
              <option value="asc">تصاعدي</option>
              <option value="desc">تنازلي</option>
            </select>
            <select className="form-control" value={filterActive} onChange={(e) => setFilterActive(e.target.value)} style={{ width: '160px' }}>
              <option value="all">الكل</option>
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
            </select>
            <button className="btn btn-success btn-sm" onClick={onCreateNew}>
              ➕ إضافة VPS جديد
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <span>❌</span>
            <span>{error}</span>
          </div>
        )}

        {vpsList.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🖥️</div>
            <div className="empty-state-title">لا توجد خوادم VPS</div>
            <div className="empty-state-text">ابدأ بإضافة خادم VPS جديد</div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>اسم مختصر</th>
                    <th>IP</th>
                    <th>User</th>
                    <th>Provider</th>
                    <th>عدد العملاء</th>
                    <th>تاريخ البدء</th>
                    <th>تاريخ الانتهاء</th>
                    <th>الحالة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((vps) => (
                    <tr key={vps._id}>
                      <td>{vps.nickname || '—'}</td>
                      <td><strong>{vps.ip}</strong></td>
                      <td>{vps.user}</td>
                      <td>{vps.provider || '—'}</td>
                      <td>
                        <span className="badge badge-info">{vps.customerCount || 0}</span>
                      </td>
                      <td>
                        {vps.startDate
                          ? new Date(vps.startDate).toLocaleDateString('en-US')
                          : '—'}
                      </td>
                      <td>
                        {vps.endDate
                          ? new Date(vps.endDate).toLocaleDateString('en-US')
                          : '—'}
                      </td>
                      <td>
                        <span className={`badge ${vps.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {vps.isActive ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => onVPSSelect(vps)}
                        >
                          التفاصيل
                        </button>
                        <button
                          className={`btn btn-outline-secondary btn-sm ${copiedSSHId === vps._id ? 'btn-success' : ''}`}
                          style={{ marginLeft: '0.5rem' }}
                          onClick={() => handleCopySSH(vps)}
                        >
                          {copiedSSHId === vps._id ? '✓ تم النسخ' : '📋 نسخ SSH'}
                        </button>
                        <button
                          className={`btn btn-outline-secondary btn-sm ${copiedPwdId === vps._id ? 'btn-success' : ''}`}
                          style={{ marginLeft: '0.5rem' }}
                          onClick={() => handleCopyPassword(vps)}
                        >
                          {copiedPwdId === vps._id ? '✓ تم النسخ' : '📋 نسخ كلمة المرور'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  السابق
                </button>
                <span style={{ padding: '0.5rem 1rem', color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                  صفحة {pagination.page} من {pagination.pages}
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                >
                  التالي
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VPSList;
